import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { useAuth, isWriter } from "@/context/AuthContext";
import { PageHeader, Section, Metric, EmptyState } from "@/components/primitives";
import { ProjectJourney } from "@/components/ProjectJourney";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

export default function Overview() {
  const { currentId, current } = useProjects();
  const { user } = useAuth();
  const [journey, setJourney] = useState([]);
  const [overview, setOverview] = useState(null);
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!currentId) return;
    setLoading(true);
    try {
      const [j, o, f] = await Promise.all([
        api.get(`/api/projects/${currentId}/journey`),
        api.get(`/api/projects/${currentId}/overview`),
        api.get(`/api/projects/${currentId}/finance`),
      ]);
      setJourney(j.data);
      setOverview(o.data);
      setFinance(f.data);
    } finally {
      setLoading(false);
    }
  }, [currentId]);

  useEffect(() => { load(); }, [load]);

  const changePhaseStatus = async (phaseId, status) => {
    try {
      await api.put(`/api/journey/${phaseId}`, { status });
      setJourney((prev) => prev.map((p) => (p.id === phaseId ? { ...p, status } : p)));
      toast.success("Phase updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const changePhaseAudiences = async (phaseId, audiences) => {
    try {
      await api.put(`/api/journey/${phaseId}`, { audiences });
      setJourney((prev) => prev.map((p) => (p.id === phaseId ? { ...p, audiences } : p)));
      toast.success("Audience updated");
    } catch {
      toast.error("Update failed");
    }
  };

  if (!currentId || loading) {
    return <div className="cm-label animate-pulse">Loading…</div>;
  }

  const fundingContracted = finance?.funding?.total_contracted ?? null;
  const currentBudget = finance?.budget_versions?.find((b) => b.label?.includes("Revised"))?.amount ?? null;
  const actuals = finance?.recorded_actuals?.amount ?? null;

  return (
    <div>
      <PageHeader
        eyebrow={`Project · ${current?.source_title || ""}`}
        title={current?.name}
        description="A start-to-closeout view of the film for investors and creators. Historical records are not proof that a phase is complete; unverified items are labelled explicitly."
        testid="overview-header"
      >
        <StatusBadge status={current?.status} />
      </PageHeader>

      <Section title="Financial snapshot">
        <div className="grid grid-cols-1 gap-px bg-[#2C2C2A] sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="Funding contracted" value={money(fundingContracted)} note="Total contracted & wired" />
          <Metric label="Current budget (Jul 20)" value={money(currentBudget)} note="Revised budget version" />
          <Metric label="Recorded actuals (Jul 18)" value={money(actuals)} note="Interim — not final" />
          <Metric label="Bank-verified cash" value={null} note="Pending closeout verification" />
        </div>
        <p className="mt-3 text-xs text-[#A1A19D]">
          Final verified spending and cash remaining are not yet determinable.{" "}
          <Link to="/finance" className="text-[#F59E0B] hover:underline">Open the Finance module →</Link>
        </p>
      </Section>

      <Section title="Project Journey — Development to Closeout">
        <ProjectJourney phases={journey} editable={isWriter(user)} onChangeStatus={changePhaseStatus} onChangeAudiences={changePhaseAudiences} />
      </Section>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <Section title={`Unresolved closeout items (${overview?.closeout_open_count ?? 0})`}>
          {overview?.open_requests?.length ? (
            <ul className="space-y-3">
              {overview.open_requests.map((r) => (
                <li key={r.id} className="border border-[#2C2C2A] bg-[#1A1A18] p-4" data-testid={`overview-closeout-${r.index}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm text-[#F9F9F8]">{r.title}</div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="cm-label mt-2">Due {r.due_date} · Owner {r.owner || "Unassigned"}</div>
                </li>
              ))}
              <Link to="/closeout" className="inline-block text-sm text-[#F59E0B] hover:underline">View all closeout requests →</Link>
            </ul>
          ) : (
            <EmptyState title="No open items" description="All closeout requests are resolved." />
          )}
        </Section>

        <Section title="Production & Post status">
          {overview?.production?.length ? (
            <ul className="space-y-2">
              {overview.production.map((p) => (
                <li key={p.id} className="flex items-center justify-between border border-[#2C2C2A] bg-[#1A1A18] px-4 py-3" data-testid={`overview-prod-${p.order}`}>
                  <span className="text-sm text-[#F9F9F8]">{p.name}</span>
                  <StatusBadge status={p.status} />
                </li>
              ))}
              <Link to="/production" className="inline-block text-sm text-[#F59E0B] hover:underline">Open Production & Post →</Link>
            </ul>
          ) : (
            <EmptyState title="No workstreams" />
          )}
        </Section>
      </div>
    </div>
  );
}
